import { v4 as Uuid } from 'uuid'
import { jclone } from '@/etc/utils'
import { factory } from '../state'

export default {
  setStripProp: (state, { tuuid, suuid, code, value }) => {
    if (state._pushUndo) state._pushUndo()
    if (!state.tracks[tuuid]) return
    const strip = state.tracks[tuuid].strips[suuid]
    if (!strip) return
    strip[code] = value
    state.tuned = false
  },
  upsertClipEnvelope: (state, { tuuid, suuid, prop, envelope }) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    if (!strip.clipEnvelopes || typeof strip.clipEnvelopes !== 'object') strip.clipEnvelopes = {}
    strip.clipEnvelopes[prop] = {
      ...(strip.clipEnvelopes[prop] || {}),
      ...(envelope || {}),
    }
    state.tuned = false
  },
  // Phase 3: select/clear Smart Region
  selectSmartRegion: (state, { tuuid, suuid, prop, region }) => {
    state.timeline.activeTrack = tuuid || state.timeline.activeTrack
    state.timeline.activeStrip = suuid || state.timeline.activeStrip
    state.selectedSmartRegion = { tuuid, suuid, prop, region }
  },
  clearSmartRegion: (state) => {
    state.selectedSmartRegion = null
  },
  // Phase 4: upsert Smart Key Group for a property on a strip
  upsertSmartKeys: (state, { tuuid, suuid, prop, group }) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    if (!strip.smartKeys) strip.smartKeys = {}
    strip.smartKeys[prop] = {
      ...(strip.smartKeys[prop] || {}),
      ...(group || {})
    }
    state.tuned = false
  },
  // Phase 4: remove Smart Key Group
  removeSmartKeys: (state, { tuuid, suuid, prop }) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip) return
    if (strip.smartKeys && strip.smartKeys[prop])
      delete strip.smartKeys[prop]
    state.tuned = false
  },
  // Phase 8: infer outer Smart Key Group for a prop from existing keyframes
  convertKeysToSmartGroup: (state, { tuuid, suuid, prop }) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip || !strip.animation) return
    if (prop === 'transform') {
      const kfs = jclone(strip.animation.keyframes).sort((a, b) => a.time - b.time)
      const entryKf = kfs.find(k => k.type === 'entry')
      const exitKf = kfs.find(k => k.type === 'exit')
      const asset = factory.asset(strip.asset.uuid)
      const def = (asset?.defaultProps || {})
      const getOr = (obj, key, d) => (obj && typeof obj[key] === 'number') ? obj[key] : d
      const makeMatrixFrom = (kf) => ({
        position: {
          x: getOr(kf?.props, 'position.x', def['position.x'] || 0),
          y: getOr(kf?.props, 'position.y', def['position.y'] || 0),
          z: getOr(kf?.props, 'position.z', def['position.z'] || 0)
        },
        rotation: {
          x: getOr(kf?.props, 'rotation.x', def['rotation.x'] || 0),
          y: getOr(kf?.props, 'rotation.y', def['rotation.y'] || 0),
          z: getOr(kf?.props, 'rotation.z', def['rotation.z'] || 0)
        },
        scale: {
          x: getOr(kf?.props, 'scale.x', def['scale.x'] || 1),
          y: getOr(kf?.props, 'scale.y', def['scale.y'] || 1),
          z: getOr(kf?.props, 'scale.z', def['scale.z'] || 1)
        }
      })
      const startM = makeMatrixFrom(entryKf)
      const hasAny = (k) => ['position.x','position.y','position.z','rotation.x','rotation.y','rotation.z','scale.x','scale.y','scale.z']
        .some(code => typeof (k.props || {})[code] !== 'undefined')
      const firstPoint = kfs.find(k => k.type !== 'entry' && hasAny(k))
      const entryOff = firstPoint ? firstPoint.time : 0
      const leftM = firstPoint ? makeMatrixFrom(firstPoint) : startM
      const filtered = kfs.filter(k => k.type !== 'exit' && hasAny(k))
      const lastBeforeExit = filtered.length ? filtered[filtered.length - 1] : null
      const rightM = lastBeforeExit ? makeMatrixFrom(lastBeforeExit) : leftM
      const exitTime = strip.length
      const lastTime = lastBeforeExit ? lastBeforeExit.time : exitTime
      const exitOff = Math.max(0, exitTime - lastTime)
      const endM = exitKf ? makeMatrixFrom(exitKf) : rightM
      if (!strip.smartKeys) strip.smartKeys = {}
      strip.smartKeys.transform = {
        start: startM,
        left: leftM,
        right: rightM,
        end: endM,
        offsets: { entry: entryOff, exit: exitOff },
        links: { rightToLeft: false },
        sustainEasing: 'Linear.None',
        entryEasing: 'Linear.None',
        exitEasing: 'Linear.None'
      }
      state.tuned = false
      return
    }
    const kfs = jclone(strip.animation.keyframes).sort((a, b) => a.time - b.time)
    const entryKf = kfs.find(k => k.type === 'entry')
    const exitKf = kfs.find(k => k.type === 'exit')
    const asset = factory.asset(strip.asset.uuid)
    const def = (asset?.defaultProps || {})
    const startVal = (entryKf?.props && typeof entryKf.props[prop] !== 'undefined')
      ? entryKf.props[prop]
      : (def[prop])
    // first non-entry keyframe with this prop → landing value and entry offset
    const firstPoint = kfs.find(k => k.type !== 'entry' && typeof (k.props || {})[prop] !== 'undefined')
    const leftVal = firstPoint ? firstPoint.props[prop] : (typeof startVal !== 'undefined' ? startVal : 0)
    const entryOff = firstPoint ? firstPoint.time : 0
    // last before exit → right value; exit offset from strip end
    const filtered = kfs.filter(k => k.type !== 'exit' && typeof (k.props || {})[prop] !== 'undefined')
    const lastBeforeExit = filtered.length ? filtered[filtered.length - 1] : null
    const rightVal = lastBeforeExit ? lastBeforeExit.props[prop] : leftVal
    const exitTime = strip.length
    const lastTime = lastBeforeExit ? lastBeforeExit.time : exitTime
    const exitOff = Math.max(0, exitTime - lastTime)
    const endVal = (exitKf?.props && typeof exitKf.props[prop] !== 'undefined')
      ? exitKf.props[prop]
      : (typeof startVal !== 'undefined' ? startVal : rightVal)
    if (!strip.smartKeys) strip.smartKeys = {}
    strip.smartKeys[prop] = {
      start: { value: startVal },
      left: { value: leftVal },
      right: { value: rightVal },
      end: { value: endVal },
      offsets: { entry: entryOff, exit: exitOff },
      links: { rightToLeft: false },
      sustainEasing: 'Linear.None',
      entryEasing: 'Linear.None',
      exitEasing: 'Linear.None'
    }
    state.tuned = false
  },
  // Phase 8: bake Smart Key Group back to keyframes for a prop
  bakeSmartGroupToKeys: (state, { tuuid, suuid, prop }) => {
    const track = state.tracks[tuuid]
    if (!track) return
    const strip = track.strips[suuid]
    if (!strip || !strip.animation) return
    const group = (strip.smartKeys || {})[prop]
    if (!group) return
    if (prop === 'transform') {
      const kfs = jclone(strip.animation.keyframes)
      const exitIdx = kfs.findIndex(k => k.type === 'exit')
      if (exitIdx < 0) return
      const entryIdx = kfs.findIndex(k => k.type === 'entry')
      const applyMatrixTo = (kf, mat) => {
        if (!kf.props) kf.props = {}
        if (!kf.delay) kf.delay = {}
        const set = (code, val) => { kf.props[code] = val; if (typeof kf.delay[code] === 'undefined') kf.delay[code] = 0 }
        set('position.x', mat.position.x)
        set('position.y', mat.position.y)
        set('position.z', mat.position.z)
        set('rotation.x', mat.rotation.x)
        set('rotation.y', mat.rotation.y)
        set('rotation.z', mat.rotation.z)
        set('scale.x', mat.scale.x)
        set('scale.y', mat.scale.y)
        set('scale.z', mat.scale.z)
      }
      if (entryIdx >= 0) applyMatrixTo(kfs[entryIdx], group.start)
      const entryOff = Math.max(0, group.offsets?.entry || 0)
      if (entryOff > 0) {
        const existIdx = kfs.findIndex(k => Math.abs(k.time - entryOff) < 1e-6)
        if (existIdx >= 0) applyMatrixTo(kfs[existIdx], group.left)
        else kfs.push({ uuid: Uuid(), type: 'point', removable: true, time: entryOff, delay: {}, props: {} })
        if (existIdx < 0) applyMatrixTo(kfs[kfs.length - 1], group.left)
      }
      const exitTime = strip.length
      const exitOff = Math.max(0, group.offsets?.exit || 0)
      const exitStart = Math.max(0, exitTime - exitOff)
      if (exitStart >= 0 && exitStart <= exitTime) {
        const existIdx2 = kfs.findIndex(k => Math.abs(k.time - exitStart) < 1e-6 && k.type !== 'exit')
        const linked = !!(group.links && group.links.rightToLeft)
        const rightMat = linked ? group.left : group.right
        if (existIdx2 >= 0) applyMatrixTo(kfs[existIdx2], rightMat)
        else kfs.push({ uuid: Uuid(), type: 'point', removable: true, time: exitStart, delay: {}, props: {} })
        if (existIdx2 < 0) applyMatrixTo(kfs[kfs.length - 1], rightMat)
      }
      applyMatrixTo(kfs[exitIdx], group.end)
      strip.animation.keyframes = kfs.sort((a, b) => a.time - b.time)
      delete strip.smartKeys.transform
      state.tuned = false
      return
    }
    const kfs = jclone(strip.animation.keyframes)
    const exitIdx = kfs.findIndex(k => k.type === 'exit')
    if (exitIdx < 0) return
    const entryIdx = kfs.findIndex(k => k.type === 'entry')
    // Ensure entry keyframe props/delay
    if (entryIdx >= 0) {
      if (!kfs[entryIdx].props) kfs[entryIdx].props = {}
      if (!kfs[entryIdx].delay) kfs[entryIdx].delay = {}
      kfs[entryIdx].props[prop] = typeof group.start?.value !== 'undefined' ? group.start.value : (kfs[entryIdx].props[prop] || 0)
      kfs[entryIdx].delay[prop] = Math.max(0, group.offsets?.entry || 0)
      if (!kfs[entryIdx].easing) kfs[entryIdx].easing = {}
      if (!kfs[entryIdx].easing[prop]) kfs[entryIdx].easing[prop] = 'Linear.None'
    }
    // Insert/update point at entryOff with left value
    const entryOff = Math.max(0, group.offsets?.entry || 0)
    if (entryOff > 0) {
      const existIdx = kfs.findIndex(k => Math.abs(k.time - entryOff) < 1e-6)
      if (existIdx >= 0) {
        if (!kfs[existIdx].props) kfs[existIdx].props = {}
        kfs[existIdx].props[prop] = typeof group.left?.value !== 'undefined' ? group.left.value : kfs[existIdx].props[prop]
        if (!kfs[existIdx].delay) kfs[existIdx].delay = {}
        kfs[existIdx].delay[prop] = Math.max(0, (kfs[existIdx].delay[prop] || 0))
      } else {
        kfs.push({ uuid: Uuid(), type: 'point', removable: true, time: entryOff, delay: {}, props: { [prop]: group.left?.value } })
      }
    }
    // Insert/update point at exitStart with right value and tween to exit
    const exitTime = strip.length
    const exitOff = Math.max(0, group.offsets?.exit || 0)
    const exitStart = Math.max(0, exitTime - exitOff)
    if (exitStart >= 0 && exitStart <= exitTime) {
      const existIdx2 = kfs.findIndex(k => Math.abs(k.time - exitStart) < 1e-6 && k.type !== 'exit')
      const linked = !!(group.links && group.links.rightToLeft)
      const rightValLinked = linked
        ? (typeof group.left?.value !== 'undefined' ? group.left.value : undefined)
        : (typeof group.right?.value !== 'undefined' ? group.right.value : undefined)
      if (existIdx2 >= 0) {
        if (!kfs[existIdx2].props) kfs[existIdx2].props = {}
        kfs[existIdx2].props[prop] = typeof rightValLinked !== 'undefined' ? rightValLinked : kfs[existIdx2].props[prop]
        if (!kfs[existIdx2].delay) kfs[existIdx2].delay = {}
        kfs[existIdx2].delay[prop] = Math.max(0, exitOff)
        if (!kfs[existIdx2].easing) kfs[existIdx2].easing = {}
        if (!kfs[existIdx2].easing[prop]) kfs[existIdx2].easing[prop] = 'Linear.None'
      } else {
        kfs.push({ uuid: Uuid(), type: 'point', removable: true, time: exitStart, delay: { [prop]: exitOff }, easing: { [prop]: 'Linear.None' }, props: { [prop]: rightValLinked } })
      }
    }
    // Ensure exit keyframe value
    if (!kfs[exitIdx].props) kfs[exitIdx].props = {}
    kfs[exitIdx].props[prop] = typeof group.end?.value !== 'undefined' ? group.end.value : (kfs[exitIdx].props[prop] || kfs[exitIdx].props[prop])
    // Persist back
    strip.animation.keyframes = kfs.sort((a, b) => a.time - b.time)
    // Remove SmartKey group so UI switches back to legacy row
    try {
      if (strip.smartKeys && strip.smartKeys[prop]) delete strip.smartKeys[prop]
    } catch (e) { /* noop */ }
    state.tuned = false
  },
  activeSmartKey: (state, next) => {
    state.timeline.activeSmartKey = next
  },
}

